// Broker Placement card — the customer/broker-facing surface for cyber-insurance
// placement. Wraps LossExceedanceChart with a placement signal rail + recommendation
// and the load-bearing disclaimer (risk-informed loss model, NOT a priced PML).
// Phase 1: no per-peril decomposition / peril Iris note — those wait on Roger's
// allocation math. Driven entirely by the backend `placement` block.
import { useEffect, useState } from 'react'
import styles from './BrokerPlacementCard.module.css'
import type { Placement } from './types'
import { LossExceedanceChart } from './LossExceedanceChart'
import { formatMoneyShort, formatPct } from './chartGeometry'

type BrokerPlacementCardProps = {
  placement: Placement
  companyName?: string
  className?: string
}

// Distinct dark-readable hues for the per-peril segments. Color is decorative — every
// segment is also text-labeled (peril + amount) in the rows below, per the color-blind rule.
const PERIL_COLORS = ['#00d9ff', '#5eead4', '#7c9cff', '#f0a868', '#c084fc']

function buildRecommendation(p: Placement): { big: string; sub: string } {
  const fmt = formatMoneyShort
  const { coverageLimit, recommendedExcess, limitToP95, limitToP99 } = p
  if (coverageLimit != null && recommendedExcess != null && recommendedExcess > 0) {
    const tail =
      limitToP95 != null && limitToP99 != null && limitToP99 > limitToP95
        ? ` A further ${fmt(limitToP99 - limitToP95)} layer reaches the 99th (${fmt(limitToP99)}).`
        : ''
    return {
      big: `${fmt(coverageLimit)} primary + ${fmt(recommendedExcess)} excess`,
      sub: `Attaches the tower at the 95th-percentile severe event (${fmt(limitToP95)}).${tail}`,
    }
  }
  if (coverageLimit != null && recommendedExcess === 0) {
    return {
      big: `${fmt(coverageLimit)} tower — adequate to the 95th percentile`,
      sub: 'The current limit already meets the modeled 95th-percentile severe event.',
    }
  }
  if (limitToP95 != null) {
    return {
      big: `Place to ${fmt(limitToP95)} (95th percentile)`,
      sub: 'No current tower on file; the model recommends placing to the 95th-percentile severe event.',
    }
  }
  return { big: '—', sub: 'Insufficient data to recommend a placement.' }
}

export function BrokerPlacementCard({
  placement,
  companyName,
  className,
}: BrokerPlacementCardProps) {
  const { coverageLimit, exceedanceAtLimit, returnPeriodYrs, limitToP95, limitToP99, expectedAnnualLoss } =
    placement
  const reco = buildRecommendation(placement)
  const returnLabel = returnPeriodYrs != null ? `~1-in-${returnPeriodYrs} yr` : '—'
  const cardClass = [styles.card, className].filter(Boolean).join(' ')

  // Click-to-enlarge for the (otherwise cramped) exceedance chart.
  const [enlarged, setEnlarged] = useState(false)
  useEffect(() => {
    if (!enlarged) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEnlarged(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [enlarged])

  return (
    <section className={cardClass} aria-label="Broker placement analysis">
      <div className={styles.client}>
        <div className={styles.eyebrow}>Account · prepared for placement</div>
        <h3 className={styles.title}>{companyName || 'Account'}</h3>
        <div className={styles.chips}>
          {coverageLimit != null && (
            <span className={`${styles.chip} ${styles.chipTower}`}>
              Current tower <b>{formatMoneyShort(coverageLimit)}</b>
            </span>
          )}
          <span className={styles.chip}>
            Calibration <b>risk-informed loss model</b>
          </span>
        </div>
      </div>

      <div className={styles.grid}>
        {/* Exceedance curve */}
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <div className={styles.headTitle}>Loss Exceedance &amp; Tower Placement</div>
            <div className={styles.headRight}>
              <span className={styles.headSub}>P(annual loss ≥ limit)</span>
              <button
                type="button"
                className={styles.enlarge}
                aria-label="Enlarge chart"
                title="Enlarge chart"
                onClick={() => setEnlarged(true)}
              >
                ⤢
              </button>
            </div>
          </div>
          <div className={styles.chartWrap}>
            <LossExceedanceChart placement={placement} />
          </div>
          <div className={styles.legend}>
            <span>
              <i className={styles.swatch} style={{ borderTopColor: '#00d9ff' }} />
              Exceedance probability
            </span>
            {coverageLimit != null && (
              <span>
                <i className={styles.box} style={{ background: 'rgba(255,68,68,.18)' }} />▲ Current tower
              </span>
            )}
            {placement.recommendedExcess != null && placement.recommendedExcess > 0 && (
              <span>
                <i className={styles.box} style={{ background: 'rgba(0,255,136,.18)' }} />● Recommended excess
              </span>
            )}
          </div>
        </div>

        {/* Placement signal rail */}
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <div className={styles.headTitle}>Placement Signal</div>
            <div className={styles.headSub}>vs. modeled loss</div>
          </div>
          <div className={styles.rail} data-testid="placement-rail">
            <div className={styles.stat}>
              <div className={styles.statK}>
                Exceedance at current limit<em>{formatMoneyShort(coverageLimit)} tower</em>
              </div>
              <div className={`${styles.statV} ${styles.warn}`}>{formatPct(exceedanceAtLimit)}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statK}>
                Loss exceeds tower<em>frequency</em>
              </div>
              <div className={`${styles.statV} ${styles.warn}`}>{returnLabel}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statK}>
                Limit to reach 95th pct<em>severe-event</em>
              </div>
              <div className={`${styles.statV} ${styles.acc}`}>{formatMoneyShort(limitToP95)}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statK}>
                Limit to reach 99th pct<em>tail</em>
              </div>
              <div className={styles.statV}>{formatMoneyShort(limitToP99)}</div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statK}>
                Expected annual loss<em>ALE · mean</em>
              </div>
              <div className={styles.statV}>{formatMoneyShort(expectedAnnualLoss)}</div>
            </div>

            <div className={styles.reco} data-testid="placement-reco">
              <div className={styles.recoLbl}>Recommended placement</div>
              <div className={styles.recoBig}>{reco.big}</div>
              <div className={styles.recoSub}>{reco.sub}</div>
            </div>
          </div>
        </div>
      </div>

      {placement.perilSplit && placement.perilSplit.perils.length > 0 && (
        <div className={styles.perils} data-testid="peril-split">
          <div className={styles.panelHead}>
            <div className={styles.headTitle}>Per-Peril Decomposition</div>
            <div className={styles.headSub}>
              severe-event tail · {formatMoneyShort(placement.perilSplit.tail)}
            </div>
          </div>
          <div className={styles.pbar} role="img" aria-label="Per-peril share of the severe-event tail">
            {placement.perilSplit.perils.map((p, i) => (
              <span
                key={p.peril}
                className={styles.pseg}
                style={{ width: `${(p.share * 100).toFixed(2)}%`, background: PERIL_COLORS[i % PERIL_COLORS.length] }}
                title={`${p.label} — ${formatMoneyShort(p.amount)}`}
              />
            ))}
          </div>
          <ul className={styles.prows}>
            {placement.perilSplit.perils.map((p, i) => (
              <li key={p.peril} className={styles.prow}>
                <i className={styles.pswatch} style={{ background: PERIL_COLORS[i % PERIL_COLORS.length] }} />
                <span className={styles.plabel}>{p.label}</span>
                <span className={styles.pamt}>{formatMoneyShort(p.amount)}</span>
                <span className={styles.pshare}>{formatPct(p.share)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.footer}>
        <b>Methodology.</b> Loss exceedance modeled via Big&nbsp;6 gap-driven Monte Carlo;
        percentiles read from the simulated annual-loss distribution. Figures are a{' '}
        <b>risk-informed loss model for placement support — not a priced PML</b>; the carrier
        underwrites independently.
      </div>

      {enlarged && (
        <div className={styles.backdrop} onClick={() => setEnlarged(false)}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-label="Loss Exceedance and Tower Placement — enlarged"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHead}>
              <div className={styles.headTitle}>Loss Exceedance &amp; Tower Placement</div>
              <button
                type="button"
                className={styles.modalClose}
                aria-label="Close enlarged chart"
                onClick={() => setEnlarged(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <LossExceedanceChart placement={placement} />
            </div>
            <div className={styles.modalLegend}>
              <span>
                <i className={styles.swatch} style={{ borderTopColor: '#00d9ff' }} />
                Exceedance probability
              </span>
              {coverageLimit != null && (
                <span>
                  <i className={styles.box} style={{ background: 'rgba(255,68,68,.18)' }} />▲ Current tower
                </span>
              )}
              {placement.recommendedExcess != null && placement.recommendedExcess > 0 && (
                <span>
                  <i className={styles.box} style={{ background: 'rgba(0,255,136,.18)' }} />● Recommended excess
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
